import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Grid, Typography, TextField, Switch, Stack, Button, FormGroup, FormControlLabel,
  Checkbox, Chip, Alert, IconButton, Tooltip,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PageHeader from '../../components/common/PageHeader';
import { loadSettings, updateSetting } from './settingsSlice';
import { fetchExports, requestExport, fetchExportDetail, downloadExportResult } from '../../services/api/dataExportService';
import { tokens } from '../../theme/theme';

const EXPORT_SCOPES = ['tickets', 'incidents', 'assets'];
const STATUS_COLOR = { QUEUED: tokens.severity.medium, READY: tokens.severity.low, FAILED: tokens.severity.critical };

const SETTING_UI = {
  'security.session_timeout_minutes': {
    label: 'Session timeout (minutes)',
    helper: 'Automatically sign out an idle session after this many minutes.',
    inputProps: { min: 5, max: 1440, step: 1 },
  },
  'security.mfa_enforced': {
    label: 'Require multi-factor authentication (MFA)',
    helper: 'Require all users in this organization to complete MFA during sign-in.',
  },
  'security.password_min_length': {
    label: 'Minimum password length',
    helper: 'Minimum number of characters required for a new password.',
    inputProps: { min: 14, max: 256, step: 1 },
  },
  'security.ip_allowlist_enabled': {
    label: 'Restrict sign-in to an IP allowlist',
    helper: 'When enabled, sign-in is limited to configured approved IP addresses.',
  },
  'general.timezone': {
    label: 'Default timezone',
    helper: 'Timezone used for organization-wide dates and times.',
  },
};

const settingMeta = (setting) => {
  const explicit = SETTING_UI[setting.key] || {};
  return {
    label: explicit.label || setting.description || setting.label || String(setting.key || '').replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    helper: explicit.helper || '',
    inputProps: explicit.inputProps,
  };
};

export default function SettingsPage() {
  const dispatch = useDispatch();
  const { items } = useSelector((s) => s.platformSettings);
  const [exports, setExports] = useState([]);
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [exportError, setExportError] = useState(null);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => { dispatch(loadSettings()); }, [dispatch]);

  const loadExports = () => fetchExports().then(setExports).catch((err) => setExportError(err?.response?.data?.message || 'Could not load export jobs.'));
  useEffect(() => { loadExports(); }, []);

  const toggleScope = (scope) => setSelectedScopes((s) => (s.includes(scope) ? s.filter((x) => x !== scope) : [...s, scope]));

  const handleRequestExport = async () => {
    if (!selectedScopes.length) return setExportError('Select at least one data scope to export.');
    setExportError(null);
    setRequesting(true);
    try {
      await requestExport(selectedScopes.join(','));
      setSelectedScopes([]);
      loadExports();
    } catch (err) {
      setExportError(err?.response?.data?.message || 'Could not request this export.');
    } finally {
      setRequesting(false);
    }
  };

  const handleDownload = async (job) => {
    setExportError(null);
    try {
      const detail = await fetchExportDetail(job.id);
      downloadExportResult(detail);
    } catch (err) {
      setExportError(err?.response?.data?.message || 'Could not download this export.');
    }
  };

  const byCategory = items.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <Box>
      <PageHeader title="Settings & Security" subtitle="Platform-wide configuration — session policy, MFA enforcement, password rules" />
      <Grid container spacing={2.5}>
        {Object.entries(byCategory).map(([category, settings]) => (
          <Grid item xs={12} md={6} key={category}>
            <Paper sx={{ p: 2.4 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>{category}</Typography>
              <Stack spacing={2} sx={{ mt: 1.5 }}>
                {settings.map((s) => {
                  const meta = settingMeta(s);
                  const isBoolean = s.value === 'true' || s.value === 'false';
                  return (
                    <Box key={s.key}>
                      {isBoolean ? (
                        <>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={s.value === 'true'}
                                onChange={(e) => dispatch(updateSetting({ key: s.key, value: String(e.target.checked) }))}
                              />
                            )}
                            label={meta.label}
                            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: tokens.text.primary } }}
                          />
                          {meta.helper && <Typography sx={{ fontSize: '0.68rem', color: tokens.text.muted, ml: 6 }}>{meta.helper}</Typography>}
                        </>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          label={meta.label}
                          helperText={meta.helper}
                          type={meta.inputProps ? 'number' : 'text'}
                          inputProps={meta.inputProps}
                          defaultValue={s.value}
                          onBlur={(e) => dispatch(updateSetting({ key: s.key, value: e.target.value }))}
                        />
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2.4, mt: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Data Exports</Typography>
          <Tooltip title="Refresh"><IconButton size="small" onClick={loadExports}><RefreshRoundedIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
        <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, mb: 1.5 }}>
          Request a bulk export of your organization's data for compliance or backup purposes.
        </Typography>
        {exportError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>{exportError}</Alert>}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <FormGroup row>
            {EXPORT_SCOPES.map((scope) => (
              <FormControlLabel
                key={scope}
                control={<Checkbox size="small" checked={selectedScopes.includes(scope)} onChange={() => toggleScope(scope)} />}
                label={scope}
                sx={{ textTransform: 'capitalize' }}
              />
            ))}
          </FormGroup>
          <Button variant="contained" size="small" onClick={handleRequestExport} disabled={requesting}>
            {requesting ? 'Requesting…' : 'Request Export'}
          </Button>
        </Stack>
        <Stack spacing={1}>
          {exports.map((job) => (
            <Box key={job.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.2, borderRadius: 1.5, border: `1px solid ${tokens.border}` }}>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: tokens.text.primary, textTransform: 'capitalize' }}>{job.scope.replaceAll(',', ', ')}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: tokens.text.muted }}>{job.completedAt ? new Date(job.completedAt).toLocaleString() : 'Not completed yet'}</Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={job.status} size="small" sx={{ color: STATUS_COLOR[job.status], border: `1px solid ${STATUS_COLOR[job.status]}`, backgroundColor: 'transparent' }} />
                {job.status === 'READY' && (
                  <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownload(job)}><DownloadRoundedIcon fontSize="small" /></IconButton></Tooltip>
                )}
              </Stack>
            </Box>
          ))}
          {!exports.length && <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, textAlign: 'center', py: 2 }}>No exports requested yet.</Typography>}
        </Stack>
      </Paper>
    </Box>
  );
}
